import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useDialogDataContext } from "@/providers/DialogDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { dialogsConfig } from "./unified-items/configs/dialogsConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  Dialog,
  dialogStore,
} from "@/lib/stores/dialogStore";
import { formatRelativeTime, convertTagIdsToNames } from "@/lib/utils";
import { getStatusColor } from "@/lib/utils/statusColors";
import GlossaryWidget from "@/components/cards/widgets/ObjectsWidget";

/**
 * DialogPage - SOURCE OF TRUTH for dialog operations
 * 
 * This page owns all dialog-specific:
 * - Store operations (write/update/delete in dialogStore)
 * - CRUD handlers (create, update, delete dialogs)
 * - Card transformation (raw Dialog → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of dialogs changes, update THIS file.
 */
export default function DialogPage() {
  usePageAccessGuard('dialogs');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();
  const { createDialogItem, updateDialogItem, deleteDialogItem, refreshDialogs } = useDialogDataContext();

  // #region Store Operations
  /**
   * Dialog-specific store operations
   * These define how data is written to/from the dialogStore
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: Dialog[]) => {
      dialogStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<Dialog>) => {
      const current = dialogStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      dialogStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = dialogStore.getData();
      dialogStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Dialog-specific CRUD handlers
   * These handle server communication for dialogs
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<Dialog> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const dialogData = {
        ...data,
        game_id: data.game_id || selectedGame.id,
        folder: folderId || data.folder || null,
      };

      const createdDialog = await createDialogItem(dialogData);
      return createdDialog;
    },

    onUpdate: async (id: string, data: Partial<Dialog>): Promise<Dialog> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingDialog = dialogStore.getData().find(item => item.id === id);
      if (!existingDialog) {
        throw new Error("Dialog not found");
      }

      const dialogData = {
        ...existingDialog,
        ...data,
        id,
        game_id: data.game_id || selectedGame.id,
      };

      const updatedDialog = await updateDialogItem(dialogData);
      return updatedDialog;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteDialogItem(id, selectedGame.id);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteDialogItem(id, selectedGame.id);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, createDialogItem, updateDialogItem, deleteDialogItem, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform dialog items to CardData for display
   */
  const transformToCard = useCallback((dialog: Dialog, context: TransformContext): CardData => {
    const statusColor = getStatusColor(dialog.status, 'dialogs', dialog.game_id);
    
    return {
      id: dialog.id,
      gameId: dialog.game_id,
      title: dialog.name,
      creator: "System",
      tags: convertTagIdsToNames(dialog.tags, context.tags),
      description: dialog.description || `Dialog Key: ${dialog.dialog_key}`,
      lastUpdate: formatRelativeTime(dialog.updated_at),
      version: 1.0,
      icon: "fa-comments",
      iconBg: "#6366F1",
      shadowType: dialog.status ? "status" : "black",
      shadowColor: statusColor,
      statusColor: statusColor,
      hasVersionMismatch: false,
      status: dialog.status,
      folder: dialog.folder,
      itemType: 'dialogs',
    };
  }, []);

  /**
   * Render a dialog card using GlossaryWidget
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <GlossaryWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        owner={card.owner}
        tags={card.tags}
        description={card.description}
        lastUpdate={card.lastUpdate}
        version={card.version}
        status={card.status}
        folder={card.folder as string}
        itemType="dialogs"
        icon={card.icon}
        iconBg={card.iconBg}
        shadowType={card.shadowType}
        shadowColor={card.shadowColor}
        hasVersionMismatch={card.hasVersionMismatch}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for dialogs - navigate to detail page
   */
  const onEdit = useCallback((id: string, data: Dialog[], navigateFn: (path: string) => void) => {
    // Navigate to dialog detail page
    const dialog = data.find(item => item.id === id);
    navigate(`/dialogs/${id}`, { state: { itemName: dialog?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Dialog-specific wizard callbacks
   * Define what happens after wizard create/update operations
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: Dialog) => {
      toast({
        title: "Success",
        description: `Dialog "${item.name}" created successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await refreshDialogs(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh dialogs after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: Dialog) => {
      toast({
        title: "Success",
        description: `Dialog "${item.name}" updated successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await refreshDialogs(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh dialogs after update:", error);
        }
      }
    },

    onWizardClose: () => {
      // Any cleanup needed when wizard closes
    },
  }), [toast, selectedGame, refreshDialogs]);
  // #endregion

  // #region Combined PageHandlers
  /**
   * Combine all handlers into PageHandlers interface
   */
  const pageHandlers: PageHandlers<Dialog> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: false,
      supportsBulkCreate: false,
      supportsFlowView: true, // Dialogs support flow visualization
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="dialogs"
      handlers={pageHandlers}
      config={dialogsConfig}
    />
  );
}
